<template>
  <div id="profile-module" class="border border-solid d-theme-border-grey-light rounded relative overflow-hidden body-module-height">
    <bds-scroll-area component-class="content-scroll-area">
      <div class="bg-white p-1">
        <validation-observer ref="userDataInfoRef" v-slot="{ invalid }" slim>
          <div v-if="loaded">
            <div class="vx-row w-full">
              <validation-provider name="birthdate" slim>
                <div class="vx-col w-full md:w-1/2">
                  <bds-label :text="$t('pages.users.info.birthdate')"/>
                  <flat-pickr v-model="dataInfo.birthDate" :config="{ dateFormat: 'd F Y', maxDate: 'today' }" class="w-full" @on-change="changeData"/>
                </div>
              </validation-provider>

              <div class="vx-col w-full md:w-1/2">
                <!-- Gender -->
                <bds-label :text="$t('pages.users.info.gender')" />
                <div class="my-4">
                  <vs-radio v-model="dataInfo.gender" vs-value="male" class="mr-4" @change="changeData">{{ $t('lists.gender.male') }}</vs-radio>
                  <vs-radio v-model="dataInfo.gender" vs-value="female" class="mr-4" @change="changeData">{{ $t('lists.gender.female') }}</vs-radio>
                </div>
              </div>
            </div>

            <!-- Languages -->
            <div class="vx-row w-full">
              <div class="vx-col w-full md:w-1/2">
                <bds-label :text="$t('pages.users.info.language')" />
                <v-select :options="languageChoices" label="text" v-model="dataInfo.language" :reduce="language => language.value" :clearable="false" @input="changeData">
                  <template #selected-option="{ text, icon }">
                    <div style="display: flex; align-items: baseline;">
                      <i class="mr-2 flag-icon" :class="icon"/>
                      <label>{{ text }}</label>
                    </div>
                  </template>

                  <template slot="option" slot-scope="option">
                    <span class="flag-icon" :class="option.icon"></span>
                    {{ option.text }}
                  </template>
                </v-select>
              </div>

              <div class="vx-col w-full md:w-1/2">
                <bds-label :text="$t('pages.users.info.currency')" />
                <v-select :options="currencyChoices" label="text" v-model="dataInfo.currency" :reduce="currency => currency.value"
                          :clearable="false" @input="changeData" :disabled="!$store.state.Profile.isAdmin()">

                  <template #selected-option="{ text, icon }">
                    <div style="display: flex; align-items: baseline;">
                      <i class="mr-2 flag-icon" :class="icon"/>
                      <label>{{ text }}</label>
                    </div>
                  </template>

                  <template #option="option">
                    <span class="flag-icon" :class="option.icon"></span>
                    {{ option.text }}
                  </template>
                </v-select>
              </div>
            </div>

            <!-- Mobile Number -->
            <div class="vx-row w-full">
              <div class="vx-col w-full md:w-1/2">
                <bds-label :text="$t('pages.users.info.mobile')" :mandatory="!phoneValid"/>
                <vue-tel-input v-model="dataInfo.mobile.number" @input="inputPhone" @validate="validatePhone" @focus="phoneTouched = true" :dropdownOptions="mobileOptions"
                               mode="international" :class="{ 'border border-danger': !phoneValid && phoneTouched }">
                  <template #arrow-icon="{open}">
                    <span class="vti__dropdown-arrow">{{ open ? "▲" : "▼" }}</span>
                  </template>

                  <template #icon-right>
                    <span class="vti__dropdown-arrow"> </span>
                  </template>
                </vue-tel-input>

                <bds-error :text_error="$t('pages.users.info.message.invalidPhoneFormat')" v-if="!phoneValid"/>

                <vs-checkbox v-model="dataInfo.mobile.whatsapp" @change="changeData" class="mt-2">
                  <bds-label :text="$t('pages.users.info.whatsapp')" />
                </vs-checkbox>
              </div>

              <div class="vx-col w-full md:w-1/2 flex flex-row items-center">
                <validation-provider name="typeDocument" :rules="documentTypeRule" immediate v-slot="{ errors, invalid }" slim>
                  <div class="w-1/3">
                    <bds-label :text="$t('pages.users.info.typeDocument')" :mandatory="invalid"/>
                    <div class="flex flex-col">
                      <vs-radio v-model="dataInfo.typeOfDocument" vs-name="typeDocument" vs-value="cpf" @change="changeData"
                                class="p-1" v-if="showTypeDocument('cpf')"> {{ $t('fields.cpf') }} </vs-radio>
                      <vs-radio v-model="dataInfo.typeOfDocument" vs-name="typeDocument" vs-value="cnpj" @change="changeData"
                                class="p-1" v-if="showTypeDocument('cnpj')"> {{ $t('fields.cnpj') }} </vs-radio>
                      <vs-radio v-model="dataInfo.typeOfDocument" vs-name="typeDocument" vs-value="id" @change="changeData"
                                class="p-1" v-if="showTypeDocument('id')"> {{ $t('fields.id') }} </vs-radio>
                    </div>

                    <bds-error :text_error="errors[0]"/>
                  </div>
                </validation-provider>

                <validation-provider :name="dataInfo.typeOfDocument" rules="required|numeric" immediate v-if="dataInfo.typeOfDocument"
                                     v-slot="{ errors, invalid }" slim>
                  <div class="w-2/3">
                    <bds-label :text="$t(`pages.users.info.${dataInfo.typeOfDocument}`)" :mandatory="invalid" />

                    <template v-if="dataInfo.typeOfDocument === 'cpf' || dataInfo.typeOfDocument === 'cnpj'">
                      <div class="vs-component vs-con-input-label vs-input w-full vs-input-primary">
                        <div class="vs-con-input">
                          <the-mask class="vs-inputx vs-input--input normal hasValue"
                                    :mask="[$t(`masks.${dataInfo.typeOfDocument}`)]"
                                    v-model="dataInfo.document"
                                    :placeholder="$t(`fields.${dataInfo.typeOfDocument}`)"
                                    @change.native="changeData"/>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <vs-input size="normal" class="w-full" v-model="dataInfo.document" @change="changeData" />
                    </template>

                    <bds-error :text_error="errors[0]"/>
                  </div>
                </validation-provider>
              </div>
            </div>

            <!-- BIO -->
            <div class="vx-row w-full">
              <div class="vx-col w-full">
                <bds-label :text="$t('pages.users.info.bio')" />
                <vs-textarea v-model="dataInfo.bio" counter="250" height="75px" @change="changeData"/>
              </div>
            </div>

            <div class="flex flex-wrap justify-end p-6">
              <vs-button class="mr-6" type="border" color="warning" @click="resetData">
                {{ $t('buttons.reset') }}
              </vs-button>
              <vs-button @click="submitData" :disabled="invalid"> {{ $t('buttons.save') }}</vs-button>
            </div>
          </div>
        </validation-observer>
      </div>
    </bds-scroll-area>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component';
import {DataInfo} from '@/models/User.model';
import { getLanguageChoices, getCurrencyChoices } from "@/helpers/lists";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { TheMask } from 'vue-the-mask';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';

import 'flatpickr/dist/flatpickr.css';

export default {
  name: 'user-info',
  components: {
    flatPickr,
    TheMask,
    VueTelInput,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    value: {
      type: Object,
      default: () => new DataInfo(),
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dataInfo: null,
      loaded: false,

      phoneValid: false,
      phoneTouched: false
    }
  },
  watch: {
    value() {
      if (this.value) {
        this.copyValueObject();
        this.loaded = true;
      }
    }
  },
  computed: {
    documentTypeRule() {
      if (this.required) {
        return 'required';
      } else {
        return 'dummy';
      }
    },
    phoneRole() {
      if (this.required) {
        return 'required';
      } else {
        return 'dummy';
      }
    },
    mobileOptions() {
      return {
        showFlags: true,
        showDialCodeInSelection: true,
        showDialCodeInList: true,
        showSearchBox: true
      }
    },
    languageChoices() {
      return getLanguageChoices();
    },
    currencyChoices() {
      return getCurrencyChoices();
    }
  },
  methods: {
    showTypeDocument(documentType) {
      // will show any field if non required
      if (!this.required) {
        return true;
      }

      if (this.$store.state.Profile.isAdmin()) {
        return true;
      }

      if (this.$store.state.Profile.isBRL() && ['cpf', 'cnpj'].includes(documentType)) {
        return true;
      } else if (this.$store.state.Profile.isUSD() && ['id'].includes(documentType)) {
        return true;
      } else {
        return false;
      }
    },
    copyValueObject() {
      this.dataInfo = DataInfo.FromObj(this.value);
      if (!this.dataInfo.typeOfDocument && this.$store.state.Profile.isUSD()) {
        this.dataInfo.typeOfDocument = 'id';
      }
    },
    resetData() {
      this.copyValueObject();
    },
    submitData() {
      this.$emit('submitData');
    },
    async validateDataInfo() {
      return this.$nextTick().then(() => {
        return this.$refs.userDataInfoRef.validate().then((valid) => {
          return Promise.resolve(valid);
        });
      });
    },
    inputPhone(number, phoneObject) {
      this.validatePhone(phoneObject);
    },
    validatePhone(phoneObject) {
      this.phoneValid = !!phoneObject.valid;
      if (this.phoneValid) {
        this.dataInfo.mobile.number = phoneObject.formatted;
        this.dataInfo.mobile.phoneObject = phoneObject;
        this.$emit('input', this.dataInfo);
      }
    },
    async changeData() {
      this.validateDataInfo().then((valid) => {
        if (valid) {
          this.$emit('input', this.dataInfo);
        }
      });
    },
  },
  created() {
    this.loaded = false;
  },
  mounted() {
    this.copyValueObject();
    this.loaded = true;
    this.phoneTouched = this.phoneValid;
  },
}
</script>

<style src="vue-tel-input/dist/vue-tel-input.css"> </style>
